import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import { api } from "../../../lib/api";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { Select } from "../../../ui/Select";
import { Modal } from "../../../ui/Modal";
import { PageSpinner } from "../../../ui/Spinner";
import {
  Badge,
  Panel,
  SectionTitle,
  StatTile,
  fmtMoney,
} from "./_ui";

type Props = { tenantId: string };

export function BillingTab({ tenantId }: Props) {
  const queryClient = useQueryClient();
  const [extOpen, setExtOpen] = useState<null | "agent" | "admin">(null);
  const [rechargeOpen, setRechargeOpen] = useState<null | "sms" | "signature">(
    null,
  );

  const aboQ = useQuery({
    queryKey: ["abonnement", tenantId],
    queryFn: () => api.billing.getAbonnement(tenantId),
  });
  const walletsQ = useQuery({
    queryKey: ["wallets", tenantId],
    queryFn: () => api.billing.getWallets(tenantId),
  });

  const initMut = useMutation({
    mutationFn: (jeune: boolean) =>
      api.billing.createAbonnement({ tenantId, jeuneEntreprise: jeune }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["abonnement", tenantId] }),
  });

  const abo = aboQ.data;
  const wallets = walletsQ.data ?? [];
  const walletSms = wallets.find((w) => w.type === "sms");
  const walletSign = wallets.find((w) => w.type === "signature");

  const monthlyTotal = useMemo(() => {
    if (!abo) return 0;
    const ext = abo.extensions.reduce(
      (s, e) => s + e.prixUnitaire * e.quantite,
      0,
    );
    return Number(abo.basePrix) + ext;
  }, [abo]);

  if (aboQ.isLoading || walletsQ.isLoading) return <PageSpinner />;

  if (!abo) {
    return (
      <Panel accent="warning">
        <SectionTitle icon={ICON.euro}>Abonnement MyVictor</SectionTitle>
        <p className="text-sm text-navy-600">
          Aucun abonnement initialisé pour cette agence.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() => initMut.mutate(false)}
            disabled={initMut.isPending}
          >
            Entreprise établie · 99 €/mois
          </Button>
          <Button
            variant="secondary"
            onClick={() => initMut.mutate(true)}
            disabled={initMut.isPending}
          >
            Jeune entreprise · 69 €/mois
          </Button>
        </div>
        {initMut.error ? (
          <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {initMut.error instanceof ApiClientError
              ? initMut.error.message
              : "Erreur."}
          </p>
        ) : null}
      </Panel>
    );
  }

  return (
    <div className="space-y-5">
      {/* ---- KPIs abonnement ---- */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={ICON.euro}
          tone="accent"
          label="Base"
          value={`${fmtMoney(abo.basePrix)} €`}
          caption="par mois"
        />
        <StatTile
          icon={ICON.refresh}
          tone="navy"
          label="Cycle"
          value={abo.cycle}
        />
        <StatTile
          icon={ICON.receipt}
          tone="emerald"
          label="Total mensuel"
          value={`${fmtMoney(monthlyTotal)} €`}
          caption="base + extensions"
        />
        <StatTile
          icon={ICON.badge}
          tone="amber"
          label="Statut"
          value={
            <Badge
              tone={abo.statut === "actif" ? "emerald" : "amber"}
              dot
            >
              {abo.statut.replace("_", " ")}
            </Badge>
          }
        />
      </div>

      {/* ---- Extensions ---- */}
      <Panel>
        <SectionTitle icon={ICON.plug}>Extensions</SectionTitle>
        <div className="divide-y divide-navy-100">
          <ExtensionRow
            label="Agents extra"
            prixUnitaire={2}
            quantite={
              abo.extensions.find((e) => e.type === "agent")?.quantite ?? 0
            }
            onEdit={() => setExtOpen("agent")}
          />
          <ExtensionRow
            label="Admins extra"
            prixUnitaire={15}
            quantite={
              abo.extensions.find((e) => e.type === "admin")?.quantite ?? 0
            }
            onEdit={() => setExtOpen("admin")}
          />
        </div>

        {abo.stripeCustomerId ? (
          <div className="mt-4 border-t border-navy-100 pt-3 text-[11px] text-navy-400">
            <p>
              Stripe customer :{" "}
              <span className="font-mono">{abo.stripeCustomerId}</span>
            </p>
            {abo.stripeSubscriptionId ? (
              <p>
                Subscription :{" "}
                <span className="font-mono">{abo.stripeSubscriptionId}</span>
              </p>
            ) : null}
          </div>
        ) : null}
      </Panel>

      {/* ---- Wallets ---- */}
      <div className="grid gap-4 sm:grid-cols-2">
        <WalletCard
          label="SMS"
          icon={ICON.message}
          solde={walletSms?.solde ?? 0}
          onRecharge={() => setRechargeOpen("sms")}
        />
        <WalletCard
          label="Signatures"
          icon={ICON.pen}
          solde={walletSign?.solde ?? 0}
          onRecharge={() => setRechargeOpen("signature")}
        />
      </div>

      {extOpen ? (
        <ExtensionModal
          tenantId={tenantId}
          type={extOpen}
          currentQuantite={
            abo.extensions.find((e) => e.type === extOpen)?.quantite ?? 0
          }
          onClose={() => setExtOpen(null)}
        />
      ) : null}
      {rechargeOpen ? (
        <RechargeModal
          tenantId={tenantId}
          type={rechargeOpen}
          onClose={() => setRechargeOpen(null)}
        />
      ) : null}
    </div>
  );
}

function ExtensionRow({
  label,
  prixUnitaire,
  quantite,
  onEdit,
}: {
  label: string;
  prixUnitaire: number;
  quantite: number;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-3">
      <div>
        <p className="text-sm font-semibold text-navy-800">{label}</p>
        <p className="text-xs text-navy-500">{prixUnitaire} €/mois/unité</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge tone={quantite > 0 ? "accent" : "slate"}>×{quantite}</Badge>
        <Button variant="ghost" onClick={onEdit}>
          Modifier
        </Button>
      </div>
    </div>
  );
}

function WalletCard({
  label,
  icon,
  solde,
  onRecharge,
}: {
  label: string;
  icon: React.ReactNode;
  solde: number;
  onRecharge: () => void;
}) {
  return (
    <Panel>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent-50 text-accent-600 [&>svg]:h-5 [&>svg]:w-5">
            {icon}
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
              Crédits {label}
            </p>
            <p className="mt-0.5 text-3xl font-black tracking-tight text-navy-800 tabular-nums">
              {solde}
            </p>
          </div>
        </div>
        <Button variant="secondary" onClick={onRecharge}>
          Recharger
        </Button>
      </div>
    </Panel>
  );
}

function ExtensionModal({
  tenantId,
  type,
  currentQuantite,
  onClose,
}: {
  tenantId: string;
  type: "agent" | "admin";
  currentQuantite: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [quantite, setQuantite] = useState(currentQuantite);
  const mut = useMutation({
    mutationFn: () => api.billing.setExtension(tenantId, { type, quantite }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["abonnement", tenantId] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Extension ${type === "agent" ? "agents" : "admins"} supplémentaires`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="space-y-4"
      >
        <Input
          type="number"
          label="Quantité (0 = supprimer)"
          value={quantite}
          onChange={(e) => setQuantite(Number(e.target.value))}
          min={0}
          required
        />
        <p className="text-xs text-navy-500">
          Tarif : {type === "agent" ? "2" : "15"} €/mois/unité. Modification
          appliquée immédiatement, prise en compte au prochain cycle.
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Mise à jour…" : "Valider"}
          </Button>
        </div>
        {mut.error ? (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {mut.error instanceof ApiClientError ? mut.error.message : "Erreur."}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

function RechargeModal({
  tenantId,
  type,
  onClose,
}: {
  tenantId: string;
  type: "sms" | "signature";
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [quantite, setQuantite] = useState(500);
  const [motif, setMotif] = useState<"offert" | "ajustement" | "recharge">(
    "recharge",
  );
  const [reference, setReference] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.billing.recharge({
        tenantId,
        type,
        quantite,
        motif,
        reference: reference || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets", tenantId] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Recharger crédits ${type === "sms" ? "SMS" : "Signatures"}`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="space-y-4"
      >
        <Input
          type="number"
          label="Quantité"
          value={quantite}
          onChange={(e) => setQuantite(Number(e.target.value))}
          min={1}
          required
        />
        <Select
          label="Motif"
          value={motif}
          onChange={(e) => setMotif(e.target.value as typeof motif)}
          options={[
            { value: "recharge", label: "Recharge payante" },
            { value: "offert", label: "Offert (commercial)" },
            { value: "ajustement", label: "Ajustement (correction)" },
          ]}
        />
        <Input
          label="Référence (optionnel)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="N° facture, ticket SAV…"
        />
        <div className="flex justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Recharge…" : "Recharger"}
          </Button>
        </div>
        {mut.error ? (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {mut.error instanceof ApiClientError ? mut.error.message : "Erreur."}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  euro: (
    <svg {...sp}><path d="M4 10h12" /><path d="M4 14h9" /><path d="M19 6a7.5 7.5 0 1 0 0 12" /></svg>
  ),
  refresh: (
    <svg {...sp}><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>
  ),
  receipt: (
    <svg {...sp}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1V2l-2 1-2-1-2 1-2-1-2 1-2-1z" /><path d="M8 7h8M8 11h8M8 15h5" /></svg>
  ),
  badge: (
    <svg {...sp}><circle cx="12" cy="8" r="6" /><path d="M15.5 13 17 22l-5-3-5 3 1.5-9" /></svg>
  ),
  plug: (
    <svg {...sp}><path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" /><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" /></svg>
  ),
  message: (
    <svg {...sp}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  pen: (
    <svg {...sp}><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" /></svg>
  ),
};
